import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { adminApi } from '@/api/adminApi'
import type { AdminUserSummary } from '@/api/types'
import { AdminCard, AdminInput, AdminPageHeader } from '@/components/admin/AdminUi'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'

const pageSize = 20

export default function UsersPage() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    adminApi.users({ search: search || undefined, limit: pageSize, offset })
      .then((response) => { setUsers(response.results); setCount(response.count); setError(null) })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false))
  }, [offset, search])

  return (
    <div>
      <AdminPageHeader title="Usuários" description="Consulte contas e seus workspaces. Dê dois cliques em uma linha para abrir os detalhes." />
      <form className="mb-4 flex max-w-xl gap-2" onSubmit={(event) => { event.preventDefault(); setOffset(0); setSearch(searchDraft.trim()) }}>
        <AdminInput className="min-w-0 flex-1" placeholder="Buscar por nome ou e-mail" value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} />
        <Button type="submit" variant="primary">Buscar</Button>
      </form>
      {error ? <EmptyState title="Erro ao carregar usuários" description={error} /> : loading ? <Skeleton height={280} /> : users.length === 0 ? <EmptyState title="Nenhum usuário encontrado" /> : (
        <AdminCard className="overflow-x-auto p-0">
          <table className="w-full min-w-[720px] text-left text-xs">
            <thead><tr style={{ color: 'var(--text-muted)' }}><th className="p-3">Usuário</th><th className="p-3">Status</th><th className="p-3">E-mail</th><th className="p-3">Workspaces</th><th className="p-3">Último acesso</th></tr></thead>
            <tbody>{users.map((user) => (
              <tr key={user.id} className="border-t cursor-pointer transition-colors hover:bg-[var(--bg-toolbar)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]" tabIndex={0} aria-label={`Abrir usuário ${user.name}`} onDoubleClick={() => navigate(`/admin/users/${user.id}`)} onKeyDown={(event) => { if (event.target === event.currentTarget && event.key === 'Enter') navigate(`/admin/users/${user.id}`) }} style={{ borderColor: 'var(--border)' }}>
                <td className="p-3"><Link className="font-semibold" style={{ color: 'var(--accent)' }} to={`/admin/users/${user.id}`}>{user.name}</Link><div style={{ color: 'var(--text-dim)' }}>{user.email}</div></td>
                <td className="p-3">{user.is_active ? 'Ativo' : 'Desativado'}{user.is_staff ? ' · admin' : ''}</td>
                <td className="p-3">{user.email_verified ? 'Verificado' : 'Não verificado'}</td>
                <td className="p-3">{user.active_workspace_count} ativos · {user.workspace_count} total</td>
                <td className="p-3">{user.last_login ? new Date(user.last_login).toLocaleString('pt-BR') : 'Nunca'}</td>
              </tr>
            ))}</tbody>
          </table>
        </AdminCard>
      )}
      <div className="mt-3 flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
        <span>{count} usuário(s)</span>
        <div className="flex gap-2"><Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>Anterior</Button><Button disabled={offset + pageSize >= count} onClick={() => setOffset(offset + pageSize)}>Próxima</Button></div>
      </div>
    </div>
  )
}
